import { hasPermission, type AdminAction, type AdminModule, type PermissionSet } from '@atnt/shared';

/**
 * Rights of the device registry pages (03-modules §1.2): each page needs its admin module `read`; the page actions
 * need `create` / `update` / `delete` of the same module. The monitoring pages need admin `devices` read or the
 * monitoring `device_config` view.
 */
export interface Crud {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

const crud = (p: PermissionSet, module: AdminModule): Crud => {
  const can = (action: AdminAction) => hasPermission(p, { panel: 'admin', module, action });
  return { read: can('read'), create: can('create'), update: can('update'), delete: can('delete') };
};

export function registryRights(p: PermissionSet) {
  return {
    categories: crud(p, 'device_categories'),
    locations: crud(p, 'device_locations'),
    info: crud(p, 'device_info'),
    calibration: crud(p, 'device_calibration'),
    config: crud(p, 'device_config'),
    devices: crud(p, 'devices'),
    connectors: crud(p, 'connectors'),
  };
}
export type RegistryRights = ReturnType<typeof registryRights>;

/** "Ma'lumot monitoringi" pages. */
export const canMonitorDevices = (p: PermissionSet) =>
  hasPermission(p, { panel: 'admin', module: 'devices', action: 'read' }) ||
  hasPermission(p, { panel: 'monitoring', menu: 'device_config', action: 'view' });

/** History charts (`GET /sensors/:id/history`): monitoring view of charts/dashboard/sections or admin devices read. */
export const canReadHistory = (p: PermissionSet) =>
  (['charts', 'dashboard', 'sections'] as const).some((menu) => hasPermission(p, { panel: 'monitoring', menu, action: 'view' })) ||
  hasPermission(p, { panel: 'admin', module: 'devices', action: 'read' });

/** The alarm switch goes through `PUT /alarm-rules/:id`, which needs admin `devices.update`. */
export const canEditAlarmRules = (p: PermissionSet) => hasPermission(p, { panel: 'admin', module: 'devices', action: 'update' });
